import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { examLogoUrl, placeholderImageUrl } from "../../utils/assets";
import { useDispatch, useSelector } from "react-redux";
import { useAlert } from "react-alert";
import { getAllExamTypes } from "../../actions/commonActions";
import { getUserSubscription, getUserSubscriptionList } from "../../actions/subscriptionActions";
import { getSavedSubjects } from "../../actions/subjectActions";
import { getSavedExamSubjects } from "../../actions/admin/examSubjectAction";
import { getResultMockAnalyticsData, getResults } from "../../actions/resultActions";
import { getAssessmentList } from "../../actions/assessmentActions";
import Loader from "../Loader";
import { USER_SUBSCRIPTION_RESET } from "../../constants/subscriptionConstants";
import {
  isSubscriptionCurrentlyActive,
  subscriptionMatchesExam,
} from "../../utils/examSubscription";
import store from "../../store";
import "./Dashboard.css";

const EXAM_CARD_META = {
  jamb: {
    accent: "#2563eb",
    soft: "#eff6ff",
    description: "Joint Admissions and Matriculation Board exam for university entry.",
    icon: "fa-bullseye",
  },
  "jsce-bece": {
    accent: "#7c3aed",
    soft: "#f5f3ff",
    description: "Basic Education Certificate Examination for junior secondary students.",
    icon: "fa-book",
  },
  "post-utme": {
    accent: "#ea580c",
    soft: "#fff7ed",
    description: "University post-admission screening test after JAMB.",
    icon: "fa-certificate",
  },
  waec: {
    accent: "#059669",
    soft: "#ecfdf5",
    description: "West African Senior School Certificate Examination.",
    icon: "fa-star",
  },
};

function getExamMeta(slug = "", title = "") {
  const normalized = String(slug || title).toLowerCase();
  if (normalized.includes("jamb")) return EXAM_CARD_META.jamb;
  if (normalized.includes("bece") || normalized.includes("jsce")) return EXAM_CARD_META["jsce-bece"];
  if (normalized.includes("post") || normalized.includes("utme")) return EXAM_CARD_META["post-utme"];
  if (normalized.includes("waec") || normalized.includes("wassce")) return EXAM_CARD_META.waec;
  return {
    accent: "#008753",
    soft: "#ecfdf5",
    description: "Prepare with practice questions, mocks, and performance tracking.",
    icon: "fa-graduation-cap",
  };
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function formatExpiryLabel(expireAt) {
  if (!expireAt) return null;
  const expiry = new Date(expireAt);
  if (Number.isNaN(expiry.getTime())) return null;
  const diffDays = Math.ceil((expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "expired";
  if (diffDays === 0) return "expires today";
  if (diffDays === 1) return "expires tomorrow";
  return `expires in ${diffDays} days`;
}

const Dashboard = ({ history }) => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const [examType, setExamType] = useState("");
  const [examName, setExamName] = useState("");
  const [examSlug, setExamSlug] = useState("");
  const [examLogo, setExamLogo] = useState("");
  const [mockPickerOpen, setMockPickerOpen] = useState(false);
  const [mockStarting, setMockStarting] = useState(false);

  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { userSubscription, userSubscriptionList } = useSelector((state) => state.examSubscriptions);
  const { allExamTypes, eloading } = useSelector((state) => state.commonExams);
  const examTypes = useMemo(
    () => (Array.isArray(allExamTypes) ? allExamTypes : []),
    [allExamTypes]
  );
  const { savedSubjects } = useSelector((state) => state.courseSubjects);
  const { savedSubjects: examSavedSubjects } = useSelector((state) => state.examSubjects);
  const { result, results } = useSelector((state) => state.results);
  const { assessments } = useSelector((state) => state.assessmentsList);

  useEffect(() => {
    if (isAuthenticated && user && user.mustChangePassword) {
      history.push("/force-change-password");
    }
  }, [isAuthenticated, user, history]);

  useEffect(() => {
    dispatch(getAllExamTypes());
    dispatch(getAssessmentList());
    dispatch({ type: USER_SUBSCRIPTION_RESET });
    setExamType("");
    setExamName("");
    setExamSlug("");
    setExamLogo("");
  }, [dispatch, isAuthenticated, user]);

  useEffect(() => {
    if (user?._id) {
      dispatch(getUserSubscriptionList(user._id));
      dispatch(getSavedSubjects(user._id));
      dispatch(getSavedExamSubjects(user._id));
      dispatch(getResultMockAnalyticsData(user._id));
      dispatch(getResults(user._id));
    }
  }, [dispatch, user?._id]);

  useEffect(() => {
    if (!examType || !user?._id) return;
    dispatch(getUserSubscription(user._id, examType));
  }, [dispatch, examType, user?._id]);

  const activeSubscriptions = Array.isArray(userSubscriptionList)
    ? userSubscriptionList.filter((entry) => isSubscriptionCurrentlyActive(entry))
    : [];

  /** Unique exams covered by the student's active subscription plans. */
  const subscribedExams = useMemo(() => {
    const byId = new Map();

    activeSubscriptions.forEach((subscription) => {
      const examRef = subscription?.exam;
      if (examRef && typeof examRef === "object" && examRef._id) {
        byId.set(String(examRef._id), examRef);
      }

      examTypes.forEach((exam) => {
        if (subscriptionMatchesExam(subscription, exam._id)) {
          byId.set(String(exam._id), exam);
        }
      });
    });

    return Array.from(byId.values()).filter((exam) => exam?.status !== false);
  }, [activeSubscriptions, examTypes]);

  const registeredSubjectCount = useMemo(() => {
    const primary = Array.isArray(examSavedSubjects) ? examSavedSubjects : [];
    const legacy = Array.isArray(savedSubjects) ? savedSubjects : [];
    const source = primary.length > 0 ? primary : legacy;
    return source.reduce((total, entry) => {
      const processed = Array.isArray(entry?.subjectsProcessed) ? entry.subjectsProcessed.length : 0;
      const subjectCount = processed || (Array.isArray(entry?.subjects) ? entry.subjects.length : 0);
      return total + subjectCount;
    }, 0);
  }, [examSavedSubjects, savedSubjects]);

  const mockAttempts = useMemo(() => {
    const mockAnalyticsCount = Array.isArray(result?.quizzes) ? result.quizzes.length : 0;
    const allResultsCount = Array.isArray(results) ? results.length : 0;
    return Math.max(mockAnalyticsCount, allResultsCount);
  }, [result, results]);

  const bestJambScore = useMemo(() => {
    if (!Array.isArray(result?.quizzes) || result.quizzes.length === 0) return null;
    const scores = result.quizzes.map((quiz) => {
      const accuracy = quiz.questionCount
        ? (quiz.correctCount / quiz.questionCount) * 100
        : 0;
      return Math.round((accuracy * 400) / 100);
    });
    return Math.max(...scores);
  }, [result]);

  const alerts = useMemo(() => {
    const items = [];
    activeSubscriptions.forEach((entry) => {
      const label = entry?.exam?.title || entry?.planName || "Subscription";
      const expiryLabel = formatExpiryLabel(entry?.expireAt);
      if (expiryLabel && expiryLabel !== "expired") {
        if (entry?.expireAt) {
          const formatted = new Date(entry.expireAt).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
          items.push(`${label} subscription ${expiryLabel}${expiryLabel.includes("in") ? "" : ` · ${formatted}`}`);
        } else {
          items.push(`${label} subscription ${expiryLabel}`);
        }
      }
    });

    const beceSubscription = activeSubscriptions.find((entry) =>
      String(entry?.exam?.title || entry?.exam?.slug || "")
        .toLowerCase()
        .includes("bece")
    );
    if (beceSubscription && mockAttempts === 0) {
      items.push("You have not attempted BECE mock in over 30 days");
    }

    return items.slice(0, 3);
  }, [activeSubscriptions, mockAttempts]);

  const selectExam = (item) => {
    setExamType(item._id);
    setExamName(item.title);
    setExamSlug(item.slug);
    setExamLogo(item.logo);
    localStorage.setItem("logo", item.logo);
    localStorage.setItem("maximumSubjects", item.maximumSubjects);
    localStorage.setItem("examinationType", item.examType);
    dispatch({ type: USER_SUBSCRIPTION_RESET });
    if (item.slug !== "post-utme" && user?._id) {
      dispatch(getUserSubscription(user._id, item._id));
    }
    return item;
  };

  const proceedWithExam = (item) => {
    if (!item?._id || item?.status === false) {
      alert.error("This exam type is not available yet.");
      return;
    }

    selectExam(item);
    localStorage.setItem("examId", item._id);
    localStorage.setItem("examSlug", item.slug);
    localStorage.setItem("examName", item.title);
    localStorage.setItem("logo", item.logo);

    const matchedSubscription =
      userSubscription && subscriptionMatchesExam(userSubscription, item._id)
        ? userSubscription
        : activeSubscriptions.find((entry) => subscriptionMatchesExam(entry, item._id)) || null;

    if (matchedSubscription) {
      localStorage.setItem("userSubscription", matchedSubscription._id);
      if (item.title === "JSCE (BECE)") {
        history.push("/course-subjects");
      } else {
        history.push("/categories");
      }
      return;
    }

    if (item.title === "POST UTME") {
      history.push("/university");
      return;
    }

    history.push("/subscription-plans");
  };

  const resolveMockAssessment = async () => {
    let list = Array.isArray(assessments) ? assessments : [];
    if (list.length === 0) {
      await dispatch(getAssessmentList());
      list = store.getState()?.assessmentsList?.assessments || [];
    }

    if (!Array.isArray(list)) {
      list = [];
    }

    return (
      list.find((item) => String(item?.slug || "").toLowerCase() === "mock") ||
      list.find((item) => String(item?.title || "").toLowerCase() === "mock") ||
      null
    );
  };

  const findRegistrationForExam = (examId) => {
    if (!examId) return null;

    const examKey = String(examId);
    const pools = [
      Array.isArray(examSavedSubjects) ? examSavedSubjects : [],
      Array.isArray(savedSubjects) ? savedSubjects : [],
    ];

    for (const pool of pools) {
      const match = pool.find((entry) => String(entry?.exam?._id || "") === examKey);
      if (match) return match;
    }

    return null;
  };

  const subjectIdsFromRegistration = (registration) => {
    if (!registration) return [];
    if (Array.isArray(registration.subjectsProcessed) && registration.subjectsProcessed.length) {
      return registration.subjectsProcessed.map(String).filter(Boolean);
    }
    return (registration.subjects || [])
      .map((entry) => entry?.subject?._id || entry?._id || entry)
      .map(String)
      .filter((id) => id && id !== "undefined");
  };

  const isJambExam = (exam) => {
    const slug = String(exam?.slug || "").toLowerCase();
    const title = String(exam?.title || "").toLowerCase();
    return slug.includes("jamb") || title.includes("jamb");
  };

  /**
   * Prepares exam + mock assessment context from a subscribed plan.
   * JAMB: auto-selects registered subjects and goes straight to instructions.
   * Other exams: student picks the subject on the registered-subjects screen.
   */
  const beginMockForExam = async (exam) => {
    if (!exam?._id) {
      alert.error("This exam type is not available yet.");
      return;
    }

    const subscription =
      activeSubscriptions.find((entry) => subscriptionMatchesExam(entry, exam._id)) || null;

    if (!subscription) {
      alert.info("Subscribe to an exam plan to start a mock test.");
      history.push("/my-subscriptions");
      return;
    }

    setMockStarting(true);
    try {
      selectExam(exam);
      localStorage.setItem("examId", exam._id);
      localStorage.setItem("examSlug", exam.slug || "");
      localStorage.setItem("examName", exam.title || "");
      localStorage.setItem("logo", exam.logo || "");
      localStorage.setItem("maximumSubjects", exam.maximumSubjects || "");
      localStorage.setItem("examinationType", exam.examType || "");
      localStorage.setItem("userSubscription", subscription._id);

      const examMode =
        String(exam.examType || "").toLowerCase() === "theory" ? "theory" : "objective";
      localStorage.setItem("examType", examMode);

      if ((subscription.planName || "").toLowerCase() === "trial" && subscription.numOfQuestions) {
        localStorage.setItem("questionCount", subscription.numOfQuestions);
      } else {
        localStorage.removeItem("questionCount");
      }

      const universityId =
        subscription?.university?._id ||
        (typeof subscription?.university === "string" ? subscription.university : "");
      if (universityId) {
        localStorage.setItem("universityId", universityId);
      }

      const mockAssessment = await resolveMockAssessment();
      if (!mockAssessment?._id) {
        alert.error("Mock assessment is not available right now. Please try again later.");
        return;
      }

      localStorage.setItem("assessmentId", mockAssessment._id);
      // Always mark this session as mock so subject picker hides year controls,
      // even if the assessment document uses a non-standard slug.
      localStorage.setItem("assessmentMode", "mock");
      localStorage.setItem("assessmentSlug", "mock");
      localStorage.setItem("assessmentName", "mock");

      const registration = findRegistrationForExam(exam._id);
      const subjectIds = subjectIdsFromRegistration(registration);
      const jamb = isJambExam(exam);

      if (!registration || subjectIds.length === 0) {
        alert.info(
          `Register your ${exam.title || "exam"} subjects first, then you can start a mock.`
        );
        setMockPickerOpen(false);
        history.push("/saved-subjects");
        return;
      }

      if (registration.category?._id) {
        localStorage.setItem("categoryId", registration.category._id);
      }
      if (registration.course?._id) {
        localStorage.setItem("courseId", registration.course._id);
      }
      localStorage.setItem("subjects", subjectIds.join(","));
      localStorage.setItem("processedSubjects", subjectIds.join(","));
      // This id is SavedExamSubjects — do not reuse as RegisteredSubject update id.
      localStorage.setItem("savedExamSubjectId", String(registration._id));

      // Keep UserSubscription id for mock APIs (do not replace with plan metadata).
      const registrationSubId =
        registration?.subscriptionPlan?._id ||
        registration?.subscriptionPlan ||
        subscription._id;
      if (registrationSubId) {
        localStorage.setItem("userSubscription", String(registrationSubId));
      }

      setMockPickerOpen(false);

      if (jamb) {
        // JAMB mocks use the full registered subject set — skip subject picking.
        history.push("/instructions");
        return;
      }

      // WAEC / BECE / POST UTME: student chooses the subject before starting.
      history.push("/registered-subjects");
    } catch (error) {
      console.error("Take mock failed:", error);
      alert.error("Could not start the mock test. Please try again.");
    } finally {
      setMockStarting(false);
    }
  };

  const startMockTest = () => {
    if (subscribedExams.length === 0) {
      alert.info("Subscribe to an exam plan to start a mock test.");
      history.push("/my-subscriptions");
      return;
    }

    if (subscribedExams.length === 1) {
      beginMockForExam(subscribedExams[0]);
      return;
    }

    setMockPickerOpen(true);
  };

  const firstName = user?.first_name || "Student";
  const greeting = getGreeting();

  const stats = [
    {
      label: "Try a Demo",
      value: "Free",
      subtext: "No sign-up needed",
      icon: "fa-play-circle",
      tone: "pink",
      href: "/demo",
    },
    {
      label: "Active Subscriptions",
      value: activeSubscriptions.length,
      subtext: "Plans running",
      icon: "fa-credit-card",
      tone: "green",
      href: "/my-subscriptions",
    },
    {
      label: "Registered Subjects",
      value: registeredSubjectCount,
      subtext: "Across all exams",
      icon: "fa-book",
      tone: "blue",
      href: "/saved-subjects",
    },
    {
      label: "Mock Attempts",
      value: mockAttempts,
      subtext: "All exams",
      icon: "fa-heartbeat",
      tone: "orange",
      href: "/mock-score",
    },
    {
      label: "Best JAMB Score",
      value: bestJambScore ?? "—",
      subtext: "Personal best",
      icon: "fa-line-chart",
      tone: "purple",
      href: "/mock-score",
    },
  ];

  const quickActions = [
    {
      label: "Take a Mock Test",
      icon: "fa-bolt",
      tone: "green",
      action: startMockTest,
    },
    {
      label: "View All Results",
      icon: "fa-bar-chart",
      tone: "blue",
      action: () => history.push("/results"),
    },
    {
      label: "My Subscriptions",
      icon: "fa-credit-card",
      tone: "purple",
      action: () => history.push("/my-subscriptions"),
    },
    {
      label: "Registered Subjects",
      icon: "fa-book",
      tone: "orange",
      action: () => history.push("/saved-subjects"),
    },
  ];

  return (
    <Fragment>
      <div className="student-dashboard">
        <div className="student-dashboard__hero">
          <div>
            <p className="student-dashboard__hero-kicker">{greeting},</p>
            <h1>{firstName} — ready to study?</h1>
            <p>
              You have {activeSubscriptions.length} active exam preparation
              {activeSubscriptions.length === 1 ? "" : "s"}. Keep up the momentum — every session
              gets you closer to your goal.
            </p>
          </div>
          <div className="student-dashboard__hero-actions">
            <button
              type="button"
              className="student-dashboard__btn student-dashboard__btn--light"
              onClick={startMockTest}
              disabled={mockStarting}
            >
              <i className="fa fa-bolt" /> {mockStarting ? "Starting mock…" : "Take a Mock Test"}
            </button>
            <button
              type="button"
              className="student-dashboard__btn student-dashboard__btn--ghost"
              onClick={() => history.push("/results")}
            >
              <i className="fa fa-file-text-o" /> View Results
            </button>
          </div>
        </div>

        <div className="student-dashboard__stats">
          {stats.map((item) => (
            <Link key={item.label} to={item.href} className={`student-dashboard__stat student-dashboard__stat--${item.tone}`}>
              <div className="student-dashboard__stat-icon">
                <i className={`fa ${item.icon}`} />
              </div>
              <p className="student-dashboard__stat-label">{item.label}</p>
              <p className="student-dashboard__stat-value">{item.value}</p>
              <p className="student-dashboard__stat-subtext">{item.subtext}</p>
            </Link>
          ))}
        </div>

        {alerts.length > 0 && (
          <div className="student-dashboard__alert">
            <div className="student-dashboard__alert-items">
              {alerts.map((item) => (
                <span key={item}>
                  <i className="fa fa-exclamation-circle" /> {item}
                </span>
              ))}
            </div>
            <Link to="/my-subscriptions" className="student-dashboard__alert-link">
              Manage
            </Link>
          </div>
        )}

        <div className="student-dashboard__quick-actions">
          {quickActions.map((item) => (
            <button
              key={item.label}
              type="button"
              className={`student-dashboard__quick-action student-dashboard__quick-action--${item.tone}`}
              onClick={item.action}
            >
              <span className={`student-dashboard__quick-action-icon student-dashboard__quick-action-icon--${item.tone}`}>
                <i className={`fa ${item.icon}`} />
              </span>
              <span className="student-dashboard__quick-action-label">{item.label}</span>
              <i className="fa fa-angle-right student-dashboard__quick-action-chevron" />
            </button>
          ))}
        </div>

        {mockPickerOpen && (
          <div
            className="student-dashboard__modal-backdrop"
            role="presentation"
            onClick={() => !mockStarting && setMockPickerOpen(false)}
          >
            <div
              className="student-dashboard__modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mock-exam-picker-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="student-dashboard__modal-head">
                <div>
                  <p className="student-dashboard__modal-kicker">Based on your subscriptions</p>
                  <h2 id="mock-exam-picker-title">Choose exam for mock test</h2>
                </div>
                <button
                  type="button"
                  className="student-dashboard__modal-close"
                  onClick={() => setMockPickerOpen(false)}
                  disabled={mockStarting}
                  aria-label="Close"
                >
                  <i className="fa fa-times" />
                </button>
              </div>
              <p className="student-dashboard__modal-copy">
                Only exams covered by your active plans are listed. JAMB starts with your
                registered subjects selected; other exams let you pick the subject first.
              </p>
              <div className="student-dashboard__modal-list">
                {subscribedExams.map((exam) => {
                  const meta = getExamMeta(exam.slug, exam.title);
                  const registration = findRegistrationForExam(exam._id);
                  const ready = subjectIdsFromRegistration(registration).length > 0;
                  const jamb = isJambExam(exam);
                  return (
                    <button
                      key={exam._id}
                      type="button"
                      className="student-dashboard__modal-exam"
                      style={{ "--exam-accent": meta.accent, "--exam-soft": meta.soft }}
                      onClick={() => beginMockForExam(exam)}
                      disabled={mockStarting}
                    >
                      <span className="student-dashboard__modal-exam-icon">
                        {exam.logo ? (
                          <img
                            src={examLogoUrl(exam.logo)}
                            alt=""
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = placeholderImageUrl();
                            }}
                          />
                        ) : (
                          <i className={`fa ${meta.icon}`} />
                        )}
                      </span>
                      <span className="student-dashboard__modal-exam-copy">
                        <strong>{exam.title}</strong>
                        <em>
                          {!ready
                            ? "Register subjects first, then start mock"
                            : jamb
                            ? "Subjects auto-selected — start mock"
                            : "Choose a subject, then start mock"}
                        </em>
                      </span>
                      <i className="fa fa-arrow-right" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <section className="student-dashboard__exam-section">
          <div className="student-dashboard__section-head">
            <h2>Select Exam Type</h2>
            <p>Click a card to start your preparation</p>
          </div>

          {eloading ? (
            <Loader />
          ) : (
            <div className="student-dashboard__exam-grid">
              {examTypes.map((item) => {
                const meta = getExamMeta(item.slug, item.title);
                const isActivePlan = activeSubscriptions.some((entry) =>
                  subscriptionMatchesExam(entry, item._id)
                );
                return (
                  <button
                    key={item._id}
                    type="button"
                    className={`student-dashboard__exam-card ${!item?.status ? "is-disabled" : ""}`}
                    onClick={() => proceedWithExam(item)}
                    disabled={!item?.status}
                    style={{ "--exam-accent": meta.accent, "--exam-soft": meta.soft }}
                  >
                    <div className="student-dashboard__exam-card-top">
                      <div className="student-dashboard__exam-icon">
                        {item.logo ? (
                          <img
                            src={examLogoUrl(item.logo)}
                            alt=""
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = placeholderImageUrl();
                            }}
                          />
                        ) : (
                          <i className={`fa ${meta.icon}`} />
                        )}
                      </div>
                      <div>
                        <h3>{item.title}</h3>
                        <p>{meta.description}</p>
                      </div>
                    </div>
                    <div className="student-dashboard__exam-card-footer">
                      <div className="student-dashboard__exam-card-links">
                        {item.video_link && (
                          <a
                            href={item.video_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="fa fa-play-circle" /> Show Video Guide
                          </a>
                        )}
                        {isActivePlan && <span className="student-dashboard__exam-badge">Active plan</span>}
                        {!item?.status && <span className="student-dashboard__exam-badge muted">Coming Soon</span>}
                      </div>
                      <span className="student-dashboard__exam-arrow">
                        <i className="fa fa-arrow-right" />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </Fragment>
  );
};

export default Dashboard;
